/**
 * What a document says twice.
 *
 * **Ruling 85**, 17 October 2026 (b) section 2:
 *
 * > "Ruling 83 binds what is said, not where it is stored. A grid or a troop
 * > facing is said once in a document, whichever items carry it. The crash
 * > plan, squadron and troop, with its grids and its troop facings, is said
 * > once, under Admin Instructions, where the practice card's Sqn Crash Plan
 * > sits."
 *
 * The emphasis is the whole point, and it is why this module exists rather
 * than another check beside the old ones. The checks that passed while the
 * trainee heard a repeat asked **how many items carry a value**, from a list
 * of values written out by hand, read off the orders pane. Three mistakes in
 * one line: an item is not a saying, a hand-written list cannot find a value
 * nobody thought of, and the pane is not the stream. This counts sayings, of
 * every value of a kind it knows, in the stream the reader speaks.
 *
 * **The repeats the order calls for are not repeats.** Ruling 83 allows
 * `(x2)` and the mission in orders, and the schedule marks both by giving the
 * second saying an id ending `/repeat-NN`. Those are skipped, so a grid inside
 * a mission statement is still said once by this measure.
 *
 * **Ruling 92**, 17 October 2026 (c) section 3, amends it:
 *
 * > "Ruling 85 applies to times and frequencies as well as grids and troop
 * > facings. Where the same fact is said twice, it stays in its proper place
 * > (Ruling 83) and the other place drops it. Where two different facts share
 * > a value, both stay, and the pair is recorded as an allowed exception under
 * > this ruling. A radio check names the net, not the frequency."
 *
 * So all four kinds bind, and the one way past the invariant is an exception
 * written down in `ALLOWED_REPEATS` against the ruling that allowed it. A
 * repeat that merely looks like a recorded one does not pass: the exception
 * names the nodes, and a repeat said somewhere else is a new finding.
 *
 * **Ruling 95**, 18 October 2026 (a), settled how a frequency is said: "A
 * frequency is said digit by digit, and its decimal point is said 'decimal'.
 * Nine is 'niner'." So a frequency is recognised here in that form, by the
 * reader's own word for the point, and not as the figures it is written with.
 *
 * Its last sentence killed a pattern. This module used to count a net
 * designator — F1, F2 — as a frequency, and the only "frequency said twice"
 * in the Trace 5 warning order was the **net's name**, said at the frequency
 * line and again at the radio check, which is what a radio check is for. A
 * net name is not a frequency and is no longer matched at all.
 *
 * Ruling 50: an occurrence carries its kind, its node and the width of what it
 * sat in. The value itself never leaves this module — callers group on `key`,
 * which is opaque to them and is never printed.
 */

import {
  DECIMAL_WORD,
  digitsToWords,
} from "../../modules/orders-reader/normalization/numbers";

/** What this can recognise in spoken text. */
export type SpokenValueKind = "frequency" | "grid" | "time" | "troop-facing";

/**
 * The kinds Ruling 85 binds. A second saying of one of these fails.
 *
 * All four, since Ruling 92. Block (b) held a time and a frequency open for a
 * ruling and this is it: "Ruling 85 applies to times and frequencies as well
 * as grids and troop facings."
 */
export const BINDING_VALUE_KINDS: readonly SpokenValueKind[] = Object.freeze([
  "frequency",
  "grid",
  "time",
  "troop-facing",
]);

/** A repeat a ruling allows, named by the nodes that say it. */
export interface AllowedRepeat {
  readonly kind: SpokenValueKind;
  /** Node paths, sorted. Never segment ids: re-chunking moves those. */
  readonly nodes: readonly string[];
  /** The ruling that allowed it. */
  readonly ruling: number;
  /** What makes the two facts different, in words that carry no value. */
  readonly because: string;
}

/**
 * Every repeat allowed under a ruling, and nothing else.
 *
 * Ruling 92: "Where two different facts share a value, both stay, and the pair
 * is recorded as an allowed exception under this ruling." This is that record.
 *
 * An exception is tied to the nodes that say it, so it cannot quietly widen:
 * edit the document and the exception stops matching, and the invariant fails
 * rather than passing on a pair nobody looked at. That is the intended
 * behaviour, not a fragility — a repeat is allowed because the DS read those
 * two items, not because a kind of value is excusable.
 */
export const ALLOWED_REPEATS: readonly AllowedRepeat[] = Object.freeze([
  Object.freeze({
    because:
      "the notice-to-move time and the common-tasks deadline are different facts that share a time",
    kind: "time" as const,
    nodes: Object.freeze(["WARNING-ORDER/item-12", "WARNING-ORDER/item-24"]),
    ruling: 92,
  }),
]);

/** One saying of one value. */
export interface SpokenValueOccurrence {
  /** The node that said it. A path, never text. */
  readonly nodeId: string;
  readonly kind: SpokenValueKind;
  /** Opaque identity for grouping. Never logged, exported or reported. */
  readonly key: string;
  /** Characters in the segment that carried it, for Ruling 50 reporting. */
  readonly width: number;
}

/** A value said more than once, with every node that said it. */
export interface RepeatedSpokenValue {
  readonly kind: SpokenValueKind;
  readonly count: number;
  /** Node paths, in the order they are spoken. */
  readonly at: readonly string[];
  /** The width of each carrying segment, in the same order. */
  readonly widths: readonly number[];
}

/** Anything the schedule can hand over: an id and the words to say. */
export interface SpokenSegmentLike {
  readonly id: string;
  readonly text: string;
}

/**
 * Spoken digit back to its figure, derived from the reader's own map.
 *
 * Written out by hand this said `nine`, and the reader says **niner**: the
 * figures reach the stream through `digitsToWords`, which speaks 9 as the
 * phonetic. A hand-written copy of a map that already exists was the defect
 * this module was built to answer, so it is asked rather than copied.
 */
const DIGIT_WORDS: Readonly<Record<string, string>> = Object.freeze(
  Object.fromEntries(
    digitsToWords("0123456789")
      .split(" ")
      .map((word, figure) => [word, String(figure)]),
  ),
);

/** A second saying the order itself calls for, per Ruling 83. */
const CALLED_FOR_REPEAT = /\/repeat-\d+$/;

/**
 * The cardinal points a facing can take, longest first.
 *
 * Longest first because "north" is a prefix of "north east", and a facing
 * recorded as "north" where the order said "north east" would make two
 * different facings look like one.
 */
const CARDINAL_POINTS: readonly string[] = Object.freeze([
  "north east",
  "north west",
  "south east",
  "south west",
  "north",
  "south",
  "east",
  "west",
]);

const FACING = /([a-z]+) troop faces ([a-z]+(?: [a-z]+)?)/g;

/**
 * A frequency's shape in the stream: two or three figures, the word Ruling 95
 * gives its point, three figures.
 *
 * **A net designator is not a frequency.** F1 and F2 name a net, and Ruling 92
 * ends the sentence it needed: "A radio check names the net, not the
 * frequency." Matching `F1` as a frequency made the frequency line and the
 * radio check look like one value said twice, which is the one thing a warning
 * order is supposed to do.
 *
 * **It is recognised as it is said, not as it is written.** This used to match
 * the figures a card writes, which is what the stream carried while a
 * frequency reached the voice as written. Ruling 95 of 18 October 2026 (a)
 * sends it digit by digit, so figures no longer appear and a detector looking
 * for them would quietly bind nothing. The word between the two runs comes
 * from the reader — `DECIMAL_WORD` — so the two cannot drift apart.
 */
const FREQUENCY_WHOLE_LENGTHS: readonly number[] = Object.freeze([2, 3]);
const FREQUENCY_FRACTION_LENGTH = 3;

/**
 * Runs of spoken digits, with the kind each run turns out to be.
 *
 * Grids reach the stream as digit words — `grids` in the normalization speaks
 * every figure separately — so six or more digit words in a row is a grid and
 * nothing else in this material is. A time is four digit words followed by
 * "hours", which is how `phasesDurationsAndBearings` speaks one. A frequency
 * is a short run, "decimal", and three more. A run that is none of those is
 * not a value: "the two zero zero zero series" is a series.
 */
function digitRuns(
  text: string,
): readonly { readonly kind: SpokenValueKind; readonly key: string }[] {
  const words = text.toLowerCase().split(/[^a-z]+/);
  const found: { kind: SpokenValueKind; key: string }[] = [];
  let run = "";
  /** The figures before a "decimal", while the ones after are still coming. */
  let whole: string | null = null;
  const close = (next: string): void => {
    if (whole !== null && run.length === FREQUENCY_FRACTION_LENGTH) {
      found.push({ key: `freq:${whole}.${run}`, kind: "frequency" });
    } else if (
      next === DECIMAL_WORD &&
      FREQUENCY_WHOLE_LENGTHS.includes(run.length)
    ) {
      whole = run;
      run = "";
      return;
    } else if (run.length >= 6) {
      found.push({ key: `grid:${run}`, kind: "grid" });
    } else if (run.length === 4 && next === "hours") {
      found.push({ key: `time:${run}`, kind: "time" });
    }
    whole = null;
    run = "";
  };
  for (const word of words) {
    const digit = DIGIT_WORDS[word];
    if (digit === undefined) {
      close(word);
      continue;
    }
    run += digit;
  }
  close("");
  return found;
}

/**
 * Every value of a known kind said in one piece of spoken text.
 *
 * Exported because Ruling 91 needs the same grid detector and the lesson of
 * Ruling 85 is that a second hand-written one goes wrong quietly. `key` stays
 * opaque: it carries the value and is never printed.
 */
export function spokenValuesIn(
  text: string,
): readonly { readonly kind: SpokenValueKind; readonly key: string }[] {
  return valuesIn(text);
}

/** Every value of a known kind said in one segment. */
function valuesIn(
  text: string,
): readonly { kind: SpokenValueKind; key: string }[] {
  const found: { kind: SpokenValueKind; key: string }[] = [...digitRuns(text)];
  const lowered = text.toLowerCase();
  for (const match of lowered.matchAll(FACING)) {
    const [, troop, rest] = match;
    /* v8 ignore next -- both groups are mandatory in the FACING pattern */
    if (troop === undefined || rest === undefined) continue;
    const point = CARDINAL_POINTS.find((known) => rest.startsWith(known));
    if (point === undefined) continue;
    found.push({ key: `facing:${troop}:${point}`, kind: "troop-facing" });
  }
  return found;
}

/**
 * The node a schedule id belongs to.
 *
 * `SITUATION/FRIENDLY/item-09/speech-02` is said by `SITUATION/FRIENDLY/item-09`,
 * and the chunk number moves whenever Ruling 82's rules re-split the item. An
 * exception recorded against a segment id would survive one edit and then
 * silently stop matching, so exceptions are recorded against nodes.
 */
export function nodeOfSpokenId(id: string): string {
  return id.replace(/\/speech-\d+(?:\/repeat-\d+)?$/, "");
}

/**
 * Every value said in a document, in the order it is spoken.
 *
 * Called-for repeats are skipped rather than counted, because the schedule
 * says the same words again for them and counting those would report the one
 * repeat the order asked for.
 */
export function spokenValueOccurrences(
  segments: readonly SpokenSegmentLike[],
): readonly SpokenValueOccurrence[] {
  const occurrences: SpokenValueOccurrence[] = [];
  for (const segment of segments) {
    if (CALLED_FOR_REPEAT.test(segment.id)) continue;
    for (const { key, kind } of valuesIn(segment.text)) {
      occurrences.push({
        key,
        kind,
        nodeId: segment.id,
        width: segment.text.length,
      });
    }
  }
  return occurrences;
}

/**
 * Every value this document says more than once.
 *
 * An empty result is Ruling 85 held. A caller decides which kinds fail:
 * `BINDING_VALUE_KINDS` are the ones the ruling binds today.
 */
export function repeatedSpokenValues(
  segments: readonly SpokenSegmentLike[],
): readonly RepeatedSpokenValue[] {
  const byKey = new Map<string, SpokenValueOccurrence[]>();
  for (const occurrence of spokenValueOccurrences(segments)) {
    const seen = byKey.get(occurrence.key);
    if (seen === undefined) byKey.set(occurrence.key, [occurrence]);
    else seen.push(occurrence);
  }
  const repeated: RepeatedSpokenValue[] = [];
  for (const occurrences of byKey.values()) {
    const [first] = occurrences;
    if (first === undefined || occurrences.length < 2) continue;
    repeated.push({
      at: occurrences.map(({ nodeId }) => nodeId),
      count: occurrences.length,
      kind: first.kind,
      widths: occurrences.map(({ width }) => width),
    });
  }
  return repeated;
}

/**
 * The recorded exception that allows this repeat, or null.
 *
 * Kind and nodes both have to match. Two nodes saying a time are allowed only
 * where a ruling says those two nodes may; a third node joining them is a new
 * repeat and is not covered.
 */
export function allowedRepeatFor(
  repeat: RepeatedSpokenValue,
): AllowedRepeat | null {
  const nodes = [...new Set(repeat.at.map(nodeOfSpokenId))].sort();
  return (
    ALLOWED_REPEATS.find(
      (allowed) =>
        allowed.kind === repeat.kind &&
        allowed.nodes.length === nodes.length &&
        [...allowed.nodes].sort().every((node, index) => node === nodes[index]),
    ) ?? null
  );
}

/**
 * Ruling 85 as a single answer: the binding kinds said twice.
 *
 * This is the invariant. Anything it returns is a document that must not be
 * delivered as it stands. Under Ruling 92 the only way past it is an exception
 * recorded in `ALLOWED_REPEATS`.
 */
export function valuesSaidTwice(
  segments: readonly SpokenSegmentLike[],
): readonly RepeatedSpokenValue[] {
  return repeatedSpokenValues(segments).filter(
    (repeat) =>
      BINDING_VALUE_KINDS.includes(repeat.kind) &&
      allowedRepeatFor(repeat) === null,
  );
}
